import { ConfigFileSection } from '@/types';
import React from 'react';
import { useUserCanPerformAction, UserPermissionScope } from '@/utils/hooks/useUserCanPerformAction';
import Button from 'antd/lib/button';
import {
    AppPlatform,
    AppStoreType,
    CommonNotification,
    SettingField,
    StepNotificationConfigOverride,
} from '../../types';
import { antGrey, runwayColors, rwColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Icon, {
    HistoryOutlined,
    NumberOutlined,
    PlusCircleOutlined,
    SettingOutlined,
    SlidersOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { displayTextForReleaseStepType, iconForAppNotificationId } from '../../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Switch from 'antd/lib/switch';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '../HOCs/PermissionedActionButton';
import { useConfigFileManagedMessage } from '../HOCs/ConfigFileManagedMessage';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { StepGraphStep } from '@/types/workflow';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '../appHome/DestinationCounter';
import {
    effectiveMentionLabels,
    hasSettingsOverride,
    hasTriggerOverride,
    notificationRowIndicators,
    overriddenDestinationLabel,
    overriddenSettingLabels,
    overrideSendsToPrimaryChannels,
    overrideHasChannelDifference,
    overrideHasMentionDifference,
    sortStepConfigOverrides,
} from '../../helpers/notificationOverrideHelpers';

type SwitchState = 'on' | 'off' | 'indeterminate';

interface NotificationRowProps {
    notification: CommonNotification;
    // Defaults to the app's notifications for app-scoped rows
    configFileSection?: ConfigFileSection;
    notificationsEnabled: boolean;
    switchState?: SwitchState;
    isUpdating?: boolean;
    onClickSettings: (notificationId: string) => void;
    onToggleEnabled?: (enabled: boolean) => void;
    onAddOverride?: (notificationId: string) => void;
    actionType: RunwayUserAction;
    bucketId?: string;
    permissionScope?: UserPermissionScope;
    // Optional context used to render rich tooltips (Channel/@mention with per-step overrides).
    stepGraphStepsMap?: Record<string, StepGraphStep> | null;
    destinationsByStepConfigId?: Record<string, Destination[]>;
    platform?: AppPlatform;
    appStoreType?: AppStoreType;
    availableChannelsById?: Record<string, string>;
    primaryChannelNames?: string[];
    // Only used to tell which step overrides differ, so rows without overrides can leave it out
    settingFields?: SettingField[];
}

const ICON_BUTTON_PADDING = 8;

const TriStateSwitch: React.FC<{
    state: SwitchState;
    disabled?: boolean;
    onChange?: (nextOn: boolean) => void;
}> = ({ state, disabled, onChange }) => {
    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        if (disabled || !onChange) return;
        // Indeterminate or off → turn all on; on → turn all off
        onChange(state !== 'on');
    };
    if (state === 'indeterminate') {
        return (
            <span
                onClick={handleClick}
                style={{
                    width: 44,
                    height: 22,
                    backgroundColor: rwColors.primary,
                    borderRadius: 11,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s',
                }}
            >
                <span
                    style={{
                        width: 14,
                        height: 3,
                        backgroundColor: runwayColors.white,
                        borderRadius: 1.5,
                    }}
                />
            </span>
        );
    }
    return (
        <span style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <Switch checked={state === 'on'} disabled={disabled} onChange={(_c, event) => handleClick(event)} />
        </span>
    );
};

const NotificationRow: React.FC<NotificationRowProps> = (props: NotificationRowProps) => {
    const {
        notification,
        actionType,
        bucketId,
        permissionScope,
        onToggleEnabled,
        onAddOverride,
        stepGraphStepsMap,
        destinationsByStepConfigId,
        platform,
        appStoreType,
        availableChannelsById,
        primaryChannelNames,
        isUpdating,
    } = props;
    const canAddOverride = (notification.applicableStepArchetypes?.length ?? 0) > 0;
    const notificationsEnabled = props.notificationsEnabled;
    const overrides = sortStepConfigOverrides(notification.stepConfigOverrides ?? []);

    const userHasPermission = useUserCanPerformAction(
        actionType,
        undefined,
        undefined,
        bucketId,
        undefined,
        undefined,
        permissionScope,
    );
    const configFileMessage = useConfigFileManagedMessage(
        props.configFileSection ??
            (permissionScope === UserPermissionScope.App ? ConfigFileSection.Notifications : undefined),
    );
    const userCanToggle = userHasPermission && !configFileMessage;

    const formatChannel = (name: string) => (name.startsWith('#') ? name : `#${name}`);
    const resolveChannelName = (c: { id: string; name?: string }) => availableChannelsById?.[c.id] ?? c.name ?? c.id;

    const switchState: SwitchState = props.switchState ?? (notification.enabled ? 'on' : 'off');

    const defaultChannelNames = (notification.channels ?? []).map((c) => formatChannel(resolveChannelName(c)));
    const defaultMentionList = [...(notification.mentionGroups ?? []), ...(notification.mentionUsers ?? [])];

    const channelOverrides = overrides.filter((o) => overrideHasChannelDifference(o, notification));
    const mentionOverrides = overrides.filter((o) => overrideHasMentionDifference(o, notification));
    const settingFields = props.settingFields ?? [];
    const triggerOverrides = overrides.filter((o) => hasTriggerOverride(o, notification, settingFields));
    const settingsOverrides = overrides.filter((o) => hasSettingsOverride(o, notification, settingFields));

    const {
        hasChannelOverride: hasChannel,
        hasMentionOverride: hasMention,
        hasTriggerOverride: hasTrigger,
        hasSettingsOverride: hasSettings,
    } = notificationRowIndicators(notification, settingFields);
    const hasAnyOverrideIcon = hasChannel || hasMention || hasTrigger || hasSettings;

    const tooltipLabelStyle: React.CSSProperties = { color: antGrey.grey5 };

    const renderOverrideCells = (override: StepNotificationConfigOverride, valueText: React.ReactNode) => {
        const step = stepGraphStepsMap?.[override.stepConfigId];
        const archetypeText =
            step && platform && appStoreType ?
                displayTextForReleaseStepType(step.archetype, platform, appStoreType, true)
            :   null;
        const destinations = destinationsByStepConfigId?.[override.stepConfigId] ?? [];
        return (
            <React.Fragment key={override.stepConfigId}>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {destinations.length > 0 && (
                        <DestinationCounter size={16} destinations={destinations} hideTooltip />
                    )}
                </span>
                <span style={{ whiteSpace: 'nowrap' }}>
                    {archetypeText && (
                        <>
                            <span style={tooltipLabelStyle}>{archetypeText}</span>
                            <span style={{ ...tooltipLabelStyle, margin: '0 6px' }}>|</span>
                        </>
                    )}
                    <span>{step?.stepName ?? override.stepConfigId}</span>
                </span>
                <span style={{ paddingLeft: 12, whiteSpace: 'nowrap' }}>{valueText}</span>
            </React.Fragment>
        );
    };

    const sectionDividerStyle: React.CSSProperties = {
        borderBottom: `1px solid ${antGrey.grey6}`,
        paddingBottom: 8,
    };
    const overridesLabel = (count: number) => (count === 1 ? 'Override:' : 'Overrides:');
    const overridesGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'auto auto auto',
        columnGap: 8,
        rowGap: 4,
        alignItems: 'center',
        marginTop: 4,
    };

    const channelHeaderText =
        defaultChannelNames.length > 0 ? defaultChannelNames.join(', ')
        : (primaryChannelNames?.length ?? 0) > 0 ?
            `${(primaryChannelNames ?? []).map((n) => formatChannel(n)).join(', ')} (default)`
        :   'None';
    const channelTooltip = (
        <div>
            <div style={channelOverrides.length > 0 ? sectionDividerStyle : undefined}>
                <span style={tooltipLabelStyle}>Channel:</span> {channelHeaderText}
            </div>
            {channelOverrides.length > 0 && (
                <>
                    <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>{overridesLabel(channelOverrides.length)}</div>
                    <div style={overridesGridStyle}>
                        {channelOverrides.map((o) =>
                            renderOverrideCells(
                                o,
                                [
                                    ...(o?.channels ?? []).map((c) => formatChannel(resolveChannelName(c))),
                                    ...(overrideSendsToPrimaryChannels(o, notification) ? ['Primary channel(s)'] : []),
                                    ...[overriddenDestinationLabel(o, notification)].filter(Boolean),
                                ].join(', '),
                            ),
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const mentionTooltip = (
        <div>
            <div style={mentionOverrides.length > 0 ? sectionDividerStyle : undefined}>
                <span style={tooltipLabelStyle}>@mention:</span>{' '}
                {defaultMentionList.length > 0 ? defaultMentionList.join(', ') : 'None'}
            </div>
            {mentionOverrides.length > 0 && (
                <>
                    <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>{overridesLabel(mentionOverrides.length)}</div>
                    <div style={overridesGridStyle}>
                        {mentionOverrides.map((o) => {
                            const items = effectiveMentionLabels(notification, o);
                            return renderOverrideCells(o, items.length > 0 ? items.join(', ') : 'None');
                        })}
                    </div>
                </>
            )}
        </div>
    );

    const triggerTooltip = (
        <div>
            <div style={sectionDividerStyle}>
                <span style={tooltipLabelStyle}>Triggers:</span> App-level defaults
            </div>
            <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>{overridesLabel(triggerOverrides.length)}</div>
            <div style={overridesGridStyle}>{triggerOverrides.map((o) => renderOverrideCells(o, 'Customized'))}</div>
        </div>
    );

    const settingsTooltip = (
        <div>
            <div style={sectionDividerStyle}>
                <span style={tooltipLabelStyle}>Settings:</span> App-level defaults
            </div>
            <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>{overridesLabel(settingsOverrides.length)}</div>
            <div style={overridesGridStyle}>
                {settingsOverrides.map((o) =>
                    renderOverrideCells(o, overriddenSettingLabels(o, notification, settingFields).join(', ')),
                )}
            </div>
        </div>
    );

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                paddingRight: 12,
            }}
        >
            <Space size={18} align="center">
                <Icon
                    component={iconForAppNotificationId(notification.id)}
                    style={{
                        fontSize: 16,
                        color: notificationsEnabled ? antGrey.grey7 : antGrey.grey5,
                        paddingTop: 4,
                    }}
                />
                <Text type={notificationsEnabled || notification.enabled ? undefined : 'secondary'}>
                    {notification.displayText}
                </Text>
            </Space>
            <Space size={14} align="center">
                {hasChannel && (
                    <Tooltip
                        title={channelTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <NumberOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasMention && (
                    <Tooltip
                        title={mentionTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <TeamOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasTrigger && (
                    <Tooltip
                        title={triggerTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <HistoryOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasSettings && (
                    <Tooltip
                        title={settingsTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <SlidersOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                <Divider
                    type="vertical"
                    style={{
                        margin: '0 8px',
                        height: 20,
                        borderColor: antGrey.grey6,
                        visibility: hasAnyOverrideIcon ? 'visible' : 'hidden',
                    }}
                />
                <Space size={24} align="center">
                    {userCanToggle ?
                        <TriStateSwitch
                            state={switchState}
                            disabled={!notificationsEnabled || !onToggleEnabled || isUpdating}
                            onChange={onToggleEnabled}
                        />
                    :   <Tooltip title={configFileMessage ?? DISALLOWED_ACTION_ERROR_MESSAGE} placement="topRight">
                            <span>
                                <TriStateSwitch state={switchState} disabled />
                            </span>
                        </Tooltip>
                    }
                    <Tooltip title="Settings" mouseEnterDelay={0} mouseLeaveDelay={0}>
                        <span style={{ display: 'flex', alignItems: 'center', marginInline: -ICON_BUTTON_PADDING }}>
                            <Button
                                type="text"
                                disabled={!notificationsEnabled}
                                icon={
                                    <SettingOutlined
                                        style={{ color: notificationsEnabled ? antGrey.grey7 : antGrey.grey5 }}
                                    />
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onClickSettings(notification.id);
                                }}
                            />
                        </span>
                    </Tooltip>
                    <Tooltip title={canAddOverride ? 'Add override' : ''} mouseEnterDelay={0} mouseLeaveDelay={0}>
                        <span style={{ display: 'flex', alignItems: 'center', marginInline: -ICON_BUTTON_PADDING }}>
                            <Button
                                type="text"
                                disabled={!canAddOverride || !notificationsEnabled || !onAddOverride}
                                icon={
                                    <PlusCircleOutlined
                                        style={{
                                            color:
                                                canAddOverride && notificationsEnabled ? antGrey.grey7 : antGrey.grey5,
                                        }}
                                    />
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddOverride?.(notification.id);
                                }}
                            />
                        </span>
                    </Tooltip>
                </Space>
            </Space>
        </div>
    );
};

export default NotificationRow;
